import React, {useEffect, useState} from 'react';
import {Dropdown, Row, Col} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {FormModal} from 'components/modals';
import {ModalAlert} from '@/components/alerts';
import Button from '@/components/ui/Button';
import {Icon} from '@/components/icons/Icon';
import axios from 'axios';
import {useStoreActions, useStoreState} from 'easy-peasy';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import ActionSettingsForm from './ActionSettingsForm';
import useData from '@/utils/useData';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import {buildURL} from '@/utils/urlBuilder';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import usePaginatedData from '@/utils/usePaginatedData';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';

import {ColumnDef} from '@tanstack/react-table';
import {hasPermission} from '@/utils/permissions/permissionDebug';
declare global {
    interface Window {
        URLS: Record<string, string>;
    }
}

interface ActionListViewProps {
    activeChannelId?: string;
}

interface ActionTypeChoice {
    id: number;
    name: string;
    value: string;
}

interface CreatedByUser {
    id: number;
    userDisplayName: string;
}

interface ActionEntity {
    id: number;
    title: string;
    createdBy: CreatedByUser;
    channelShortname?: string;
    lastUpdated?: string;
    createdOn: string;
    actionTypeLabel: string;
    original: {
        id: number;
        title: string;
    };
}

interface AppConfig {
    DATETIME_FORMAT_REACT: string;
}

interface StoreState {
    appConfigs: {
        items: AppConfig;
    };
    actions: {
        actionHasRule: (action: {row: {original: ActionEntity}}) => boolean;
        actionTypeChoices: ActionTypeChoice[];
    };
}

interface StoreActions {
    actions: {
        deleteOne: (id: number) => void;
        setReload: (reload: boolean) => void;
        loadActionTypeChoices: (choices: ActionTypeChoice[]) => void;
    };
}

const ActionListView = React.memo<ActionListViewProps>((
    {
        activeChannelId = undefined,
    },
) => {
    const appConfig = useStoreState((state: StoreState) => state.appConfigs.items);
    const setSearchQueryState = async (setSearchQuery: (value: string) => void, value: string, setPage: (page: number) => void) => {
        setPage(1);
        setSearchQuery(value);
    };
    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [activeActionId, setActiveActionId] = useState<number>(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<{id: number, title: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const {formatDateTime} = useFormattedDateTime();

    const deleteAction = useStoreActions((actions: StoreActions) => actions.actions.deleteOne);
    const setReloadActions = useStoreActions((actions: StoreActions) => actions.actions.setReload);

    const {
        data: actions,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.actionsResource, {
        storeResolver: 'actions',
        requestParams: {
            params: {
                channelId: activeChannelId ? parseInt(activeChannelId, 10) : undefined,
                query: searchQuery,
            },
        },
    });

    useData(window.URLS.rulesResource, {
        storeResolver: 'rules',
        actionResolver: 'loadRules',
    });
    useData(window.URLS.actionTypeChoicesResource, {
        storeResolver: 'actions',
        actionResolver: 'loadActionTypeChoices',
    });

    const actionHasRule = useStoreState((state: StoreState) => state.actions.actionHasRule);

    const canCreateActions = hasPermission('fs.action.create', userData?.permissions, true);
    const canChangeActions = hasPermission('fs.action.update', userData?.permissions, true);
    const canDeleteActions = hasPermission('fs.action.delete', userData?.permissions, true);

    const userCanAccessRulesResource = hasPermission('fs.rule.list', userData?.permissions, true);
    const userCanAccessActionsResource = hasPermission('fs.action.list', userData?.permissions, true);

    // Reset pagination when the channel context changes so each channel starts from page 1
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [activeChannelId, currentPage, setCurrentPage]);

    if ((!userCanAccessActionsResource && !loadingUserData) || (!userCanAccessRulesResource && !loadingUserData)) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_use_this_view_to_create_different_conversation_actions.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (id: number) => {
        setActiveActionId(id);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = () => {
        setActiveActionId(0);
        setSettingsModalOpen(false);
    };

    const openDeleteConfirm = (id: number, title: string): void => {
        setItemToDelete({id, title});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!itemToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = buildURL('actionDetails', {action_id: itemToDelete.id});
            await axios.delete(url);
            deleteAction(itemToDelete.id);
            setReloadActions(true);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, title: string) => {
        openDeleteConfirm(id, title);
    };

    const columns: ColumnDef<ActionEntity>[] = [
        {
            header: i18n.t('th_name.text'),
            accessorKey: 'title',
        },
        {
            header: i18n.t('th_created.text'),
            accessorKey: 'createdBy.userDisplayName',
        },
        {
            header: i18n.t('th_channel_shortname.text'),
            accessorKey: 'channelShortname',
            accessorFn: (cell: ActionEntity) => {
                if (!cell.channelShortname || cell.channelShortname === '-') {
                    return '-';
                }
                return cell.channelShortname;
            },
        },
        {
            id: 'i18ntthlastmodified1text',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: ActionEntity) => {
                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return formatDateTime(
                        cell.createdOn,
                        true,
                        {setZone: false},
                    );
                }

                return formatDateTime(
                    cell.lastUpdated,
                    true,
                    {setZone: false},
                );
            },
        },
        {
            header: i18n.t('th_type_action.text'),
            accessorKey: 'actionTypeLabel',
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => {
                const usedInRules = actionHasRule(cell.row);
                let deleteButton = (
                    <div>
                        <Dropdown.Item
                            onClick={() => deleteOne(cell.row.original.id, cell.row.original.title)}
                            disabled={usedInRules}
                            active={false}
                            data-cy="action-settings-delete-btn"
                        >
                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                        </Dropdown.Item>
                    </div>
                );

                if (usedInRules) {
                    deleteButton = (
                        <Tooltip
                            content={i18n.t('tt_this_action.text')}
                            placement="bottom"
                        >
                            {deleteButton}
                        </Tooltip>
                    );
                }

                return (
                    <div className="d-flex align-items-center justify-content-end w-100">
                        <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                            <Dropdown className="d-inline-block">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    id="settings-dropdown-1"
                                    data-cy="action-settings-toggle"
                                />

                            <Dropdown.Menu align="end">
                                {
                                    canChangeActions ? (
                                        <Dropdown.Item
                                            onClick={() => openSettingsModal(cell.row.original.id)}
                                            data-cy="action-settings-btn"
                                        >
                                            <Icon name="mdi-settings" className="mr-2" />  {i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <Dropdown.Item
                                                disabled
                                                data-cy="action-list-btn">
                                                <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                            </Dropdown.Item>
                                        </Tooltip>
                                    )
                                }
                                {canDeleteActions ? deleteButton : null}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_use_this_view_to_create_different_conversation_actions.text')} />

            <Row>
                <Col lg={3}>
                    <Form className="mb-2 d-flex align-items-center search-box" onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                    }}>
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="eval-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {
                        canCreateActions ? (
                            <Button
                                variant="info"
                                size="sm"
                                className='mb-2'
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                                data-cy="new-action-btn"
                            >
                                {i18n.t('jt_add_new_action.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className='mb-2'
                                        icon="mdi-plus"
                                        disabled
                                        data-cy="action-list-plus-btn">
                                        {i18n.t('jt_add_new_action.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            <div className="card">
                <div className="card-body">
                    <TableComponent data={actions} columns={columns} />

                    <PaginationComponent
                        pages={pages}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        hideWhenSinglePage={false}
                    />
                </div>
            </div>
            {
                settingsModalOpen ? (
                    <FormModal
                        isOpen={settingsModalOpen}
                        onClose={closeSettingsModal}
                        title={i18n.t('jt_action_settings.text')}
                    >
                        <ActionSettingsForm
                            actionId={activeActionId}
                            channelId={activeChannelId ? parseInt(activeChannelId, 10) : undefined}
                            onDone={closeSettingsModal}
                        />
                    </FormModal>
                ) : null
            }
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={itemToDelete ? i18n.t('jt_act_are_you_sure.text', {title: itemToDelete.title}) : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

ActionListView.displayName = 'ActionListView';

export default ActionListView;
